'use client';

import { LogOut, PhoneOff } from 'lucide-react';
import { toast } from 'sonner';
import { Button, ConfirmButton } from '@/ui';
import { usePoolMembership } from '@/providers/pool-membership';
import { useSoftphoneRuntime } from '@/providers/softphone-context';

export function PoolLeaveButton({ size = 'md', className }: { size?: 'md' | 'sm'; className?: string }) {
  const { leave, leaving } = usePoolMembership();
  return (
    <Button
      variant="secondary"
      size={size}
      icon={LogOut}
      loading={leaving}
      className={className}
      title="Stop taking pool calls — you can rejoin from the pool page anytime"
      onClick={() => void leave()}
    >
      Leave pool
    </Button>
  );
}

/**
 * The exit from a live stage. A real call ends through the softphone's Hang
 * up, but a seat can read "Live" with nothing behind it (the line died before
 * the bridge landed, or the tab reloaded mid-call) and the softphone then
 * offers nothing to press. Two-step so it can't be hit mid-conversation by a
 * stray click; when the softphone does hold a pool call it hangs up first,
 * then leaves. A hangup that fails still leaves: the server detaches a busy
 * member's leg rather than killing it, so the exit is safe either way and
 * is exactly what a wedged agent needs.
 */
export function PoolLeaveLineButton({ size = 'sm', className }: { size?: 'md' | 'sm'; className?: string }) {
  const { leave, leaving } = usePoolMembership();
  const softphone = useSoftphoneRuntime();
  const onPoolCall = Boolean(
    softphone?.activeCall
    && softphone.activeCall.status !== 'ended'
    && softphone.activeCall.source === 'pooled_dialer'
  );
  return (
    <ConfirmButton
      size={size}
      icon={PhoneOff}
      disabled={leaving}
      className={className}
      confirmLabel={onPoolCall ? 'Hang up and leave?' : 'Leave the line?'}
      title="Ends the call if one is up and takes you out of the pool — use it if this screen is stuck on a call that isn't there"
      onConfirm={async () => {
        if (onPoolCall && softphone) {
          try {
            await softphone.hangup();
          } catch (error) {
            toast.warning(
              `${error instanceof Error && error.message ? error.message : 'Could not hang up the line.'} Leaving the pool anyway — end the line in the softphone if it stays up.`
            );
          }
        }
        await leave();
      }}
    >
      Leave line
    </ConfirmButton>
  );
}
